import { t } from "@/i18n-js/instance";
import { useIsPaywallCustomizationPhase2 } from "@/react/hooks/useIsPaywallCustomizationPhase2";
import type {
  PaywallCustomization,
  TestimonialProperty,
} from "@/react/types/PaywallCustomization";
import { useSpacesContext } from "@circle-react/contexts";
import { usePunditUserContext } from "@circle-react/contexts";
import { normalizeAccessGroupSources } from "@circle-react-shared/AccessGroups/helpers";
import {
  DISPLAY_STYLE_DEFAULT_COLORS,
  DISPLAY_STYLE_OPTIONS,
} from "../CheckoutTab/constants";

export const emptyTestimonial: TestimonialProperty = {
  name: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.name_placeholder",
  ),
  avatar_image_url: "",
  avatar_image: "",
  quote: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.quote_placeholder",
  ),
  headline: t(
    "settings.paywalls.modal.tabs.checkout.builder.testimonials.fields.headline_placeholder",
  ),
  visible: true,
  display_style: {
    type: DISPLAY_STYLE_OPTIONS.STARS,
    color: DISPLAY_STYLE_DEFAULT_COLORS.STARS,
  },
};

export const getPaywallCustomizationDefault = (
  isEnabled: boolean,
): Partial<PaywallCustomization> | Record<string, never> => {
  if (!isEnabled) {
    return {};
  }

  return {
    testimonial_properties: {
      enabled: false,
      testimonials: [{ ...emptyTestimonial }],
    },
  };
};

export const usePaywallFormDefaultValues = () => {
  const { records: spaceRecords } = useSpacesContext();
  const isPaywallCustomizationEnabled = useIsPaywallCustomizationPhase2();

  const getDefaultSpaceIds = (
    spaceIdsSelected: any,
    spaceGroupIdsSelected: any,
  ) => {
    const newSpaceIdsSelect = [...spaceIdsSelected];
    spaceRecords.forEach((space: any) => {
      if (spaceGroupIdsSelected.includes(space.space_group_id)) {
        newSpaceIdsSelect.push(space.id);
      }
    });

    return Array.from(new Set(newSpaceIdsSelect));
  };

  const { currentCommunitySettings } = usePunditUserContext();
  const isAccessGroupsEnabled = currentCommunitySettings?.access_groups_enabled;

  const getDefaultValues = (paywallParam: any) => {
    const defaultPaywall = {
      trial_days: 0,
      trial_requires_card: false,
      can_be_repurchased: false,
      stripe_prices_attributes: [],
      mobile_price_packages_attributes: [],
      all_spaces: false,
      space_group_ids: [],
      space_ids: [],
      redirect_url_enabled: false,
      thank_you_page_enabled: true,
      post_purchase_email_enabled: true,
      self_trial_cancelable: false,
      mobile_paywall_resource_attributes: {
        id: null,
        processor_id: null,
      },
      post_purchase_email_message: t(
        "settings.paywalls.form.post_purchase_email_default_message_text",
      ),
      post_purchase_email_meta: {
        subject: t(
          "settings.paywalls.form.post_purchase_email_default_subject_text",
        ),
      },
      currency_id: "",
      conversion_tracking_js_code: "",
      access_groups_enabled: isAccessGroupsEnabled,
      access_groups: [],
      paywall_customization_attributes: {
        ...getPaywallCustomizationDefault(isPaywallCustomizationEnabled),
      },
    };

    if (!paywallParam) {
      return defaultPaywall;
    }

    return {
      ...defaultPaywall,
      ...paywallParam,
      redirect_url_enabled: !!paywallParam?.redirect_url,
      enable_trial: paywallParam?.trial_days > 0,
      enable_subscription_group: !!paywallParam?.paywall_group_id,
      space_ids: getDefaultSpaceIds(
        paywallParam.space_ids,
        paywallParam.space_group_ids,
      ),
      space_group_ids: paywallParam.space_group_ids ?? [],
      currency_id: paywallParam?.currency?.id ?? "",
      access_groups_enabled: paywallParam?.access_groups_enabled,
      access_groups: normalizeAccessGroupSources(
        paywallParam?.access_group_sources,
      ),
      paywall_customization_attributes: {
        ...(paywallParam?.paywall_customization ||
          getPaywallCustomizationDefault(isPaywallCustomizationEnabled)),
      },
    };
  };

  return {
    getDefaultValues,
  };
};
